import FlatButton from '@/components/flat-button';
import OrgLogo from '@/components/org-logo';
import Text from '@/components/text';
import useAppData from '@/hooks/use-app-data.hook';
import useThemeMediaQuery from '@/hooks/use-theme-media-query';
import useToast from '@/hooks/use-toast.hook';
import { LoadState } from '@/lib/helpers';
import { OrgBrandingModel } from '@/lib/models/org-branding.model';
import { getNeedsWelcomeMessage, setNeedsWelcomeMessage } from '@/lib/services/org.service';
import { openNewTab } from '@/lib/url-helpers';
import { getBreakpoint } from '@/theme';
import { Box, Stack, Theme } from '@mui/material';
import copy from 'copy-to-clipboard';
import { FC, useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';

interface Props {
  onClose: () => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 1,
    width: '100%',
    height: '100%',
    maxHeight: '100%',
    backgroundColor: theme.palette.background.default,
  },
  welcomeMessage: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: 20,
    width: '60%',
  },
  welcomeFooter: {
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: 16,
    paddingTop: 24,
  },
  title: {
    textAlign: 'center',
  },
  [getBreakpoint('md')]: {
    welcomeMessage: {
      width: '90%',
      padding: 18,
      '& img': {
        maxWidth: '100%',
      },
    },
  },
}));

const WelcomeMessage: FC<Props> = ({ onClose }) => {
  const styles = useStyles();
  const { small } = useThemeMediaQuery();
  const [loadState, setLoadState] = useState<LoadState>('unloaded');
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);
  const { toast } = useToast();

  /*
   * Note that we are using app data here and NOT app context branding. This is to ensure
   * the welcome message is shown, even if the org does not have valid custom branding.
   */
  const { data: branding } = useAppData<OrgBrandingModel>('org-branding');

  // @todo user manually loads a thread should clear this
  useEffect(() => {
    if (loadState !== 'unloaded' || !branding) {
      return;
    }

    setLoadState('loading');

    const { portalWelcome } = branding;

    getNeedsWelcomeMessage(portalWelcome).then((showMessage) => {
      if (!showMessage) {
        setLoadState('loaded');
        return;
      }

      setShowWelcomeMessage(true);
      setNeedsWelcomeMessage(portalWelcome);
      setLoadState('loaded');
    });
  }, [loadState, branding]);

  if (loadState !== 'loaded' || !showWelcomeMessage) {
    return;
  }

  const {
    portalWelcome: { learnMoreLink, title, message },
    supportEmail,
  } = branding || new OrgBrandingModel();
  const hasLearnMoreLink = !!learnMoreLink;
  const hasSupportEmail = !!supportEmail;

  const handleCloseWelcome = () => {
    setShowWelcomeMessage(false);
    onClose();
  };

  const handleLearnMore = () => {
    openNewTab(learnMoreLink);
  };

  const handleContactSupport = () => {
    copy(supportEmail);
    toast(`Support email address copied to clipboard`);
  };

  return (
    <Stack className={styles.overlay} gap={3} alignItems="center" justifyContent="center">
      <Stack gap={3} className={styles.welcomeMessage} alignItems="center">
        <OrgLogo />
        <Box className={styles.title}>
          <Text bold size="large">
            {title || 'Welcome to the Portal'}
          </Text>
        </Box>
        <Box>
          <Text>{message || 'What would you like to get done today?'}</Text>
        </Box>
        <Stack
          className={styles.welcomeFooter}
          gap={2}
          width="100%"
          justifyContent="flex-start"
          flexDirection={small ? 'column' : 'row'}
        >
          {hasLearnMoreLink && (
            <FlatButton
              icon="open-tab"
              onClick={handleLearnMore}
              label="Learn More"
              responsive={false}
            />
          )}
          {hasSupportEmail && (
            <FlatButton
              icon="contact-support"
              onClick={handleContactSupport}
              label="Contact Support"
              responsive={false}
            />
          )}
          <Stack direction="row" flexGrow={1} justifyContent={small ? 'flex-start' : 'flex-end'}>
            <FlatButton
              label="Close"
              onClick={handleCloseWelcome}
              color="error"
              icon="close"
              responsive={false}
            />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default WelcomeMessage;
